import { useCallback, useEffect, useState } from "react";
import {
  PageHeader,
  Badge,
  Card,
  Button,
  Spinner,
  EmptyState,
  Table,
  type TableColumn,
} from "../ds";
import { api, type Enrollment } from "../lib/api";
import { useFleetEvents } from "../lib/hooks/useFleetEvents";
import { fmtDateTime } from "../lib/format";
import { BootstrapTokens } from "../components/BootstrapTokens";

// Enrollment: neue Router schicken CSR + Token und warten. Erst nach manueller
// Bestätigung hier stellt der Server ein Cert aus → jeder Router muss einmal
// freigegeben werden (zweites Sicherheitsnetz gegen geleakte Tokens).
/** Spalten der Antragsliste. IMEI/ICCID und Eingang fliegen auf Telefon-Breite
 *  raus — entscheidend sind Wunsch-ID, Fingerprint und die beiden Knöpfe. */
function columns(
  busy: string | null,
  decide: (id: string, action: "approve" | "reject") => void,
): TableColumn<Enrollment>[] {
  return [
    {
      key: "id",
      header: "Wunsch-ID",
      cell: (e) => (
        <>
          {e.requestedDeviceId ?? "—"}
          {/* Die ID läuft schon: freigeben hieße, sie dem bisherigen Router
              wegzunehmen. Muss VOR dem Klick sichtbar sein. */}
          {e.deviceExists && (
            <Badge variant="warn" dot>
              ID bereits vergeben
            </Badge>
          )}
          {/* Auf Telefon-Breite hat der Fingerprint keine eigene Spalte mehr —
              er muss aber sichtbar bleiben, er ist der Grund der Freigabe. */}
          <span className="spy-mobile-only font-mono text-[11px] text-content-3">
            {e.csrFingerprint.slice(0, 20)}…
          </span>
        </>
      ),
    },
    { key: "model", header: "Modell", hideOnMobile: true, cell: (e) => e.hints.model ?? "—" },
    // Mit welchem Token — die schnellste Plausibilitätsprüfung vor der
    // Freigabe: erwarten wir gerade überhaupt Router aus dieser Lieferung?
    {
      key: "token",
      header: "Token",
      hideOnMobile: true,
      cell: (e) => e.tokenLabel || "—",
    },
    {
      key: "sim",
      header: "IMEI / ICCID",
      mono: true,
      hideOnMobile: true,
      cell: (e) => (
        <>
          {e.hints.imei ?? "—"}
          <br />
          {e.hints.iccid ?? ""}
        </>
      ),
    },
    {
      key: "csr",
      header: "CSR-Fingerprint",
      mono: true,
      hideOnMobile: true,
      cell: (e) => `${e.csrFingerprint.slice(0, 24)}…`,
    },
    {
      key: "created",
      header: "Eingegangen",
      hideOnMobile: true,
      cell: (e) => fmtDateTime(e.createdAt),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "actions",
      cell: (e) => (
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            variant="danger-outline"
            iconName="close"
            disabled={busy === e.id}
            onClick={() => decide(e.id, "reject")}
          >
            Ablehnen
          </Button>
          <Button
            size="sm"
            variant="success"
            iconName="check"
            loading={busy === e.id}
            onClick={() => decide(e.id, "approve")}
          >
            Freigeben
          </Button>
        </div>
      ),
    },
  ];
}

export function ApprovalsPage() {
  const [rows, setRows] = useState<Enrollment[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .listEnrollments("pending")
      .then(setRows)
      .catch(() => setRows([]));
  }, []);
  useEffect(load, [load]); // Seed; danach lädt der Fleet-Stream bei Änderung neu
  useFleetEvents({ onEnrollment: load });

  async function decide(id: string, action: "approve" | "reject") {
    const antrag = rows?.find((r) => r.id === id);
    if (
      action === "approve" &&
      antrag?.deviceExists &&
      !confirm(
        `Die Geräte-ID "${antrag.requestedDeviceId}" ist bereits vergeben.\n\n` +
          "Freigeben heißt: dieser Router übernimmt sie, der bisherige verliert " +
          "sie beim nächsten Verbinden. Normal ist das nur, wenn dasselbe Gerät " +
          "neu aufgesetzt wurde.\n\nTrotzdem freigeben?",
      )
    )
      return;

    setBusy(id);
    try {
      if (action === "approve") await api.approveEnrollment(id, antrag?.deviceExists ?? false);
      else await api.rejectEnrollment(id);
      setRows((r) => (r ? r.filter((x) => x.id !== id) : r));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Enrollment" subtitle="Anträge freigeben, Bootstrap-Token verwalten" />

      <Card title="Agent-Anmeldungen" bodyPadding={false}>
        {rows === null ? (
          <div className="flex justify-center p-10">
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            iconName="check"
            title="Keine offenen Anträge"
            description="Neue Router erscheinen hier automatisch."
          />
        ) : (
          <Table
            columns={columns(busy, (id, action) => void decide(id, action))}
            rows={rows}
            rowKey={(e) => e.id}
          />
        )}
      </Card>

      <BootstrapTokens />
    </div>
  );
}
